import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { plyxApi as api } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { GiftBar, Ladder } from "./PlyxMillions";

/**
 * PlyxMillionsWatch — `/plyx/millions/watch/:sessionId`
 * Public spectator page: see a player's climb + shower them with
 * flowers and coin gifts.
 */
export default function PlyxMillionsWatch() {
  const { sessionId } = useParams();
  const { user } = usePlyxAuth();
  const [data, setData] = useState(null);
  const [gifts, setGifts] = useState([]);
  const [err, setErr] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/plyx/millions/watch/${sessionId}`);
      setData(r.data);
      setGifts(r.data?.gifts || []);
    } catch { setErr(true); }
  }, [sessionId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const [meta, setMeta] = useState(null);
  useEffect(() => {
    api.get("/plyx/millions/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  if (err) {
    return (
      <div className="min-h-screen bg-[#0a0a14] text-white flex flex-col items-center justify-center gap-3" data-testid="millions-watch-error">
        <p>This game could not be found.</p>
        <Link to="/plyx/millions" className="underline text-[#FFB42D]">Go to Plyx Millions</Link>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#0a0a14] text-white flex items-center justify-center" data-testid="millions-watch-loading">
        <Loader2 className="w-6 h-6 animate-spin"/>
      </div>
    );
  }

  const statusLabel = {
    active: "🔴 Climbing now",
    won: "👑 Reached the top!",
    lost: "Fell off the ladder",
    cashed_out: "💰 Cashed out",
  }[data.status] || data.status;

  return (
    <div className="min-h-screen bg-[#0a0a14] text-white pb-24" data-testid="millions-watch">
      <div className="max-w-xl mx-auto px-4 pt-6">
        <Link to="/plyx/millions" className="flex items-center gap-1 text-white/60 text-sm mb-4" data-testid="millions-watch-back">
          <ArrowLeft className="w-4 h-4"/> Plyx Millions
        </Link>
        <div className="text-center mb-5">
          <div className="text-4xl mb-1">🎯</div>
          <h1 className="text-2xl font-extrabold">@{data.player?.handle}</h1>
          <p className="text-sm text-white/60">{data.category_label} ladder · {statusLabel}</p>
          <p className="text-lg font-extrabold text-[#FFB42D] mt-1" data-testid="millions-watch-rung">
            Rung {data.rung} of 15
          </p>
          {data.gift_totals?.count > 0 && (
            <p className="text-xs text-white/50 mt-1" data-testid="millions-watch-gift-totals">
              🌸 {data.gift_totals.flowers} flowers · {data.gift_totals.coins} coins gifted
            </p>
          )}
        </div>
        <div className="max-w-[220px] mx-auto mb-6">
          <Ladder ladder={data.ladder} safeRungs={[]} rung={data.rung}/>
        </div>
        <div className="text-center mb-3 text-sm font-bold">Cheer them on 🌸</div>
        {user ? (
          meta && <GiftBar sessionId={sessionId} gifts={meta.gifts} onSent={(g) => setGifts((p) => [g, ...p])}/>
        ) : (
          <p className="text-center text-sm text-white/50">
            <Link to="/plyx/login" className="underline text-[#FFB42D]">Log in</Link> to send flowers and coins.
          </p>
        )}
        {gifts.length > 0 && (
          <div className="mt-6 space-y-1.5" data-testid="millions-watch-gifts">
            {gifts.slice(0, 20).map((g) => (
              <div key={g.gift_id} className="flex items-center gap-2 rounded-xl bg-white/[0.04] px-3 py-2 text-sm">
                <span className="text-lg">{g.emoji}</span>
                <span className="font-semibold">@{g.sender_handle || "fan"}</span>
                <span className="text-white/50 text-xs ml-auto">{g.coins} coins</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
